import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Briefcase, ArrowRight, User as UserIcon, Ticket, ShieldCheck, Users, UserCheck, Mail, History } from 'lucide-react';
import { getProjectRedirectUrl } from '../utils/projectRedirect';
import { getGlobalRoleLabel, getServiceDisplayName, getServiceRoleLabel } from '../utils/roles';
import { userApi } from '../api/user';
import { AdminUserSummaryResponse } from '../types';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.globalRole === 'ROOT_ADMIN';
  const services = useMemo(() => user?.services ?? [], [user?.services]);
  const accessibleServices = useMemo(() => services.filter((service) => service.role), [services]);
  const hasAdRemovalTicket = Boolean((user as any)?.hasAdRemovalTicket);
  const [summary, setSummary] = useState<AdminUserSummaryResponse | null>(null);

  useEffect(() => {
    if (!isAdmin) return;

    let cancelled = false;

    const loadSummary = async () => {
      try {
        const response = await userApi.getUsersSummary();
        const nextSummary = response.data.result;
        if (!cancelled) {
          setSummary(nextSummary);
        }
      } catch {
        if (!cancelled) {
          setSummary(null);
        }
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const handleProjectMove = (projectCode?: string) => {
    const fallback = `${window.location.origin}/`;
    const target = getProjectRedirectUrl(user || null, fallback, projectCode);
    window.location.href = target;
  };

  const adminMenuCards = [
    { to: '/user-requests', icon: <UserCheck size={18} />, label: '유저 신청 관리', desc: '계정 승인과 서비스 신청을 관리합니다.' },
    { to: '/users', icon: <Users size={18} />, label: '유저 관리', desc: '계정, 승인, 광고제거 상태를 관리합니다.' },
    { to: '/ad-removal-management', icon: <Ticket size={18} />, label: '광고제거 관리', desc: '유저별 광고 제거 권한을 부여하거나 회수합니다.' },
    { to: '/services', icon: <Briefcase size={18} />, label: '서비스 관리', desc: '서비스와 멤버를 관리합니다.', className: 'dashboard-admin-menu-card-service' },
    { to: '/audit-logs', icon: <History size={18} />, label: '로그 관리', desc: '관리 작업 이력을 확인합니다.' },
  ];

  return (
    <div className={`fade-in dashboard-page ${isAdmin ? 'dashboard-page-admin' : 'dashboard-page-user'}`}>
      {!isAdmin && (
        <section className="card dashboard-main-card dashboard-launcher">
          <div className="dashboard-card-head">
            <div className="dashboard-card-kicker">
              <Briefcase size={14} />
              <span>바로가기</span>
            </div>
            <h2 className="dashboard-card-title">승인된 서비스로 바로 이동하세요.</h2>
            <p className="dashboard-card-copy">
              로그인 후 가장 먼저 접근할 수 있는 서비스만 모았습니다.
            </p>
          </div>

          <div className="dashboard-service-grid">
            {accessibleServices.map((service) => (
              <button
                key={service.code}
                type="button"
                className="dashboard-service-tile"
                onClick={() => handleProjectMove(service.code)}
              >
                <div className="dashboard-service-top">
                  <div>
                    <div className="dashboard-service-name">{getServiceDisplayName(service.code)}</div>
                    <div className="dashboard-service-code">{service.code}</div>
                  </div>
                  <span className="badge badge-active">{getServiceRoleLabel(service.code, service.role)}</span>
                </div>
                <div className="dashboard-service-bottom">
                  <span>서비스 진입 가능</span>
                  <span>
                    열기 <ArrowRight size={14} />
                  </span>
                </div>
              </button>
            ))}
          </div>

          {accessibleServices.length === 0 ? (
            <div className="dashboard-empty-note" style={{ marginTop: '1rem' }}>
              승인된 서비스가 없습니다. 서비스 신청 메뉴에서 접근을 요청하세요.
            </div>
          ) : (
            <div className="dashboard-launcher-note" style={{ marginTop: '1rem' }}>
              총 {accessibleServices.length}개의 승인된 서비스가 있습니다.
            </div>
          )}

          <div style={{ marginTop: '1rem' }}>
            <Link to="/service-applications" className="btn btn-primary">
              서비스 신청하기
            </Link>
          </div>
        </section>
      )}

      <section className="card dashboard-hero-card">
        <div className="dashboard-hero-grid">
          <div className="dashboard-hero-copy">
            <div className="dashboard-page-kicker">{isAdmin ? '관리자 대시보드' : '내 서비스'}</div>
            <h1 className="dashboard-hero-title" style={{ fontSize: '1.4rem', fontWeight: 700 }}>
              {user?.name}님, {isAdmin ? '관리 메뉴를 선택하세요.' : '접근 가능한 서비스입니다.'}
            </h1>
            <p className="dashboard-hero-description">
              {isAdmin
                ? '필요한 관리 화면으로 바로 이동할 수 있습니다.'
                : '승인된 활성 서비스만 바로 열 수 있습니다.'}
            </p>
          </div>

          {isAdmin ? (
            <div className="dashboard-hero-stats" style={{ gridTemplateColumns: 'repeat(3, max-content)', justifyContent: 'start' }}>
              <div className="dashboard-stat-chip">
                <span>활성 유저</span>
                <strong>{summary?.activeUserCount ?? '-'}</strong>
              </div>
              <div className="dashboard-stat-chip">
                <span>승인 대기</span>
                <strong>{summary?.pendingUserCount ?? '-'}</strong>
              </div>
              <div className="dashboard-stat-chip">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Ticket size={12} />
                  광고 제거 티켓
                </span>
                <strong>{summary?.adRemovalTicketUserCount ?? '-'}</strong>
              </div>
            </div>
          ) : (
            <div className="dashboard-hero-stats" style={{ gridTemplateColumns: 'repeat(2, max-content)', justifyContent: 'start' }}>
              <div className="dashboard-stat-chip">
                <span>접근 가능 서비스</span>
                <strong>{accessibleServices.length}</strong>
              </div>
              <div className="dashboard-stat-chip">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Ticket size={12} />
                  광고 제거
                </span>
                <strong>{hasAdRemovalTicket ? '보유' : '무료 사용자'}</strong>
              </div>
            </div>
          )}
        </div>
      </section>

      {!isAdmin ? (
        <section className="card dashboard-main-card">
          <div className="dashboard-card-head">
            <div className="dashboard-card-kicker">
              <Briefcase size={14} />
              <span>활성 서비스</span>
            </div>
            <h2 className="dashboard-card-title">승인된 서비스만 표시합니다.</h2>
          </div>

          <section className="card dashboard-side-card" style={{ marginTop: '1rem' }}>
            <div className="dashboard-card-kicker">
              <Mail size={14} />
              <span>문의 이메일</span>
            </div>
            <div style={{ marginTop: '0.85rem' }}>
              <div className="dashboard-person-name" style={{ fontSize: '0.95rem' }}>wicaduce@gmail.com</div>
              <div className="dashboard-person-email" style={{ marginTop: '0.25rem' }}>
                서비스 사용 문의가 있으면 이 주소로 연락해 주세요.
              </div>
            </div>
          </section>
        </section>
      ) : (
        <div className="dashboard-grid dashboard-grid-clean">
          <section className="card dashboard-main-card">
            <div className="dashboard-card-head">
              <div className="dashboard-card-kicker">
                <ShieldCheck size={14} />
                <span>관리 메뉴</span>
              </div>
              <h2 className="dashboard-card-title dashboard-card-title-sm">필요한 화면만 선택해 들어가세요.</h2>
            </div>

            <div className="dashboard-admin-menu-grid">
              {adminMenuCards.map((item) => (
                <Link key={item.to} to={item.to} className={`dashboard-admin-menu-card ${item.className ?? ''}`}>
                  <div className="dashboard-admin-menu-icon">{item.icon}</div>
                  <div className="dashboard-admin-menu-copy">
                    <strong>{item.label}</strong>
                    <span>{item.desc}</span>
                  </div>
                  <ArrowRight size={16} />
                </Link>
              ))}
            </div>
          </section>

          <aside className="dashboard-side-stack">
            <section className="card dashboard-side-card">
              <div className="dashboard-card-kicker">
                <UserIcon size={14} />
                <span>내 정보</span>
              </div>
              <div className="dashboard-person">
                <div className="dashboard-person-avatar">{user?.name?.charAt(0)}</div>
                <div>
                  <div className="dashboard-person-name">{user?.name}</div>
                  <div className="dashboard-person-email">{user?.email}</div>
                </div>
              </div>
              <div className="dashboard-meta-list">
                <div>
                  <span>권한</span>
                  <strong>{getGlobalRoleLabel(user?.globalRole)}</strong>
                </div>
                <div>
                  <span>승인</span>
                  <strong>{user?.approvalStatus}</strong>
                </div>
              </div>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
